import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `flat` for dense lists, `raised` for standalone panels, `glass` over art. */
  tone?: 'flat' | 'raised' | 'glass';
  /** Adds a hover lift; use only when the whole card is a link or button. */
  interactive?: boolean;
}

export function Card({ className, tone = 'flat', interactive = false, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'border-line relative overflow-hidden rounded-lg border',
        tone === 'flat' && 'bg-surface',
        tone === 'raised' && 'bg-surface-raised shadow-sm',
        tone === 'glass' && 'glass',
        interactive &&
          'ease-out-quint hover:border-line-strong transition-[transform,border-color,box-shadow] duration-[240ms] hover:-translate-y-0.5 hover:shadow-md',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-line flex items-start justify-between gap-4 border-b px-5 py-4', className)}>
      <div className="min-w-0">
        <h2 className="truncate text-lg font-semibold">{title}</h2>
        {description ? <p className="text-text-subtle mt-0.5 text-sm">{description}</p> : null}
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 py-4', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('border-line flex items-center justify-between gap-3 border-t px-5 py-3', className)}
      {...props}
    />
  );
}
